import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap";
import Select from "react-select";
import { FaPen, FaPlus, FaTrash } from "react-icons/fa";

import api from "services/api";
import { useToast } from "hooks/toast";
import { useCompanies } from "hooks/companies";
import useDebounce from "hooks/useDebounce";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import MetricCard from "components/MetricCard";
import MetricCardGroup from "components/MetricCardGroup";
import ShellCard, { TableSurface } from "components/ShellCard";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { exportCsv } from "utils/exportCsv";
import { paginate, sortRows, toggleSort } from "utils/tableHelpers";
import { reactSelectPortalProps, reactSelectStyles } from "utils/reactSelectStyles";
import {
  ConfirmModal,
  ListState,
  Pill,
  STATUS_ALL,
  STATUS_OPTIONS,
  StatusPill,
  isActive,
  matchesStatus,
  normalizeText,
  toastError,
  toastSuccess,
} from "../shared";

// Vínculos transportadora × tipo de serviço do embarcador (Cadastros Gerais),
// no padrão do Hub desde a sprint 638. API inalterada: /cadastros/vinculos-tipo-servico
// (GET com idEmbarcador e, se houver, a transportadora do topo; POST/PUT/DELETE).

const URL_BASE = "/cadastros/vinculos-tipo-servico";
const XML_ALL = "__ALL__";
const XML_OPTIONS = [
  { value: XML_ALL, label: "Todos" },
  { value: "yes", label: "Com identificação por XML" },
  { value: "no", label: "Sem identificação por XML" },
];
const TRANSP_ALL = { value: "", label: "Todas" };
const DEFAULT_SORT = { field: "transportadora", dir: "asc" };

function emptyForm() {
  return { idTransportadora: "", idTipoServico: "", xml: false, status: "active" };
}

function isXml(xml) {
  return xml === 1 || xml === true || xml === "1";
}

function nomeTransportadora(item) {
  return item.transportadoraNome || `#${item.idTransportadora}`;
}

function rotuloTipo(item) {
  return item.tipoServicoCodigo ? `${item.tipoServicoCodigo} - ${item.tipoServicoNome}` : item.tipoServicoNome || "";
}

function sortValue(row, field) {
  if (field === "transportadora") return nomeTransportadora(row);
  if (field === "tipo") return rotuloTipo(row);
  if (field === "xml") return isXml(row.xml) ? 1 : 0;
  if (field === "status") return isActive(row.status) ? 1 : 0;
  return row[field];
}

const VincularTipoServico = () => {
  const { addToast } = useToast();
  const { empresa, empresaId, hasEmpresaSelection, transportadora, transportadoraId, hasTransportadoraSelection } =
    useCompanies();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [xmlFilter, setXmlFilter] = useState(XML_ALL);
  const [transpFilter, setTranspFilter] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(searchTerm, 250);

  const [transportadoras, setTransportadoras] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);

  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    if (!hasEmpresaSelection) {
      setData([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const params = { idEmbarcador: empresaId };
      // Respeita a transportadora selecionada no topo (useCompanies).
      if (hasTransportadoraSelection) params.idTransportadora = transportadoraId;
      const result = await api.get(URL_BASE, { params });
      setData(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      setData([]);
      setLoadError("Não foi possível buscar os vínculos deste embarcador.");
      toastError(addToast, "Erro ao carregar", error, "Não foi possível buscar os vínculos deste embarcador.");
    } finally {
      setLoading(false);
    }
  }, [addToast, empresaId, hasEmpresaSelection, hasTransportadoraSelection, transportadoraId]);

  useEffect(() => {
    setData([]);
    loadData();
  }, [loadData]);

  // Opções do formulário: transportadoras do embarcador e tipos de serviço ativos.
  useEffect(() => {
    if (!hasEmpresaSelection) {
      setTransportadoras([]);
      return undefined;
    }
    let mounted = true;
    api
      .get(`/transportadoras/${empresaId}`)
      .then((res) => {
        if (!mounted) return;
        setTransportadoras(
          (Array.isArray(res.data) ? res.data : []).map((item) => ({
            value: String(item.transportadora_id),
            label: item.transportadora_fantasia || `#${item.transportadora_id}`,
          }))
        );
      })
      .catch(() => {
        if (mounted) setTransportadoras([]);
      });
    return () => {
      mounted = false;
    };
  }, [empresaId, hasEmpresaSelection]);

  useEffect(() => {
    let mounted = true;
    api
      .get("/frete/tipos-servico")
      .then((res) => {
        if (!mounted) return;
        setTiposServico(
          (Array.isArray(res.data) ? res.data : []).map((item) => ({
            value: String(item.id),
            label: item.codigo ? `${item.codigo} - ${item.nome}` : item.nome,
          }))
        );
      })
      .catch(() => {
        if (mounted) setTiposServico([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const totais = useMemo(() => {
    const ativos = data.filter((item) => isActive(item.status)).length;
    const comXml = data.filter((item) => isXml(item.xml)).length;
    const transps = new Set(data.map((item) => item.idTransportadora)).size;
    return { total: data.length, ativos, comXml, transps };
  }, [data]);

  // Transportadoras presentes na lista (filtro), em ordem alfabética.
  const transpOptions = useMemo(() => {
    const vistos = {};
    data.forEach((item) => {
      vistos[String(item.idTransportadora)] = nomeTransportadora(item);
    });
    const lista = Object.keys(vistos)
      .map((id) => ({ value: id, label: vistos[id] }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
    return [TRANSP_ALL].concat(lista);
  }, [data]);

  const filtered = useMemo(() => {
    const query = normalizeText(debouncedSearch);
    return data.filter((item) => {
      if (!matchesStatus(item.status, statusFilter)) return false;
      if (xmlFilter === "yes" && !isXml(item.xml)) return false;
      if (xmlFilter === "no" && isXml(item.xml)) return false;
      if (transpFilter && String(item.idTransportadora) !== transpFilter) return false;
      if (!query) return true;
      return normalizeText([item.transportadoraNome, item.tipoServicoNome, item.tipoServicoCodigo].filter(Boolean).join(" ")).includes(
        query
      );
    });
  }, [data, debouncedSearch, statusFilter, transpFilter, xmlFilter]);

  const sorted = useMemo(() => sortRows(filtered, sort, sortValue), [filtered, sort]);
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, xmlFilter, transpFilter, sort, pageSize]);

  const openCreate = useCallback(() => {
    setEditing(null);
    // Pré-seleciona a transportadora do topo quando há uma específica escolhida.
    setForm({ ...emptyForm(), idTransportadora: hasTransportadoraSelection ? String(transportadoraId) : "" });
    setFormErrors({});
    setFormOpen(true);
  }, [hasTransportadoraSelection, transportadoraId]);

  const openEdit = useCallback((item) => {
    setEditing(item);
    setForm({
      idTransportadora: item.idTransportadora ? String(item.idTransportadora) : "",
      idTipoServico: item.idTipoServico ? String(item.idTipoServico) : "",
      xml: isXml(item.xml),
      status: isActive(item.status) ? "active" : "inactive",
    });
    setFormErrors({});
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    if (!saving) setFormOpen(false);
  }, [saving]);

  const updateForm = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  const handleSave = async () => {
    const errors = {};
    if (!form.idTransportadora) errors.idTransportadora = "Selecione a transportadora.";
    if (!form.idTipoServico) errors.idTipoServico = "Selecione o tipo de serviço.";
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const payload = {
      idEmbarcador: empresaId,
      idTransportadora: Number(form.idTransportadora),
      idTipoServico: Number(form.idTipoServico),
      xml: form.xml,
      status: form.status === "active",
    };
    try {
      setSaving(true);
      if (editing) await api.put(`${URL_BASE}/${editing.id}`, payload);
      else await api.post(URL_BASE, payload);
      toastSuccess(
        addToast,
        editing ? "Vínculo atualizado" : "Vínculo cadastrado",
        editing ? "As alterações foram salvas." : "O vínculo foi cadastrado com sucesso."
      );
      setFormOpen(false);
      loadData();
    } catch (error) {
      toastError(addToast, "Erro ao salvar", error, "Não foi possível salvar o vínculo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      setDeleting(true);
      await api.delete(`${URL_BASE}/${toDelete.id}`);
      toastSuccess(addToast, "Vínculo excluído", "O vínculo foi removido.");
      setToDelete(null);
      loadData();
    } catch (error) {
      toastError(addToast, "Não foi possível excluir", error, "Erro ao excluir o vínculo.");
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const exportar = () =>
    exportCsv({
      filename: "vinculos_tipo_servico",
      columns: [
        { label: "Transportadora", getValue: nomeTransportadora },
        { label: "Código do tipo de serviço", key: "tipoServicoCodigo" },
        { label: "Tipo de serviço", key: "tipoServicoNome" },
        { label: "Identificação por XML", getValue: (row) => (isXml(row.xml) ? "Sim" : "Não") },
        { label: "Status", getValue: (row) => (isActive(row.status) ? "Ativo" : "Inativo") },
      ],
      rows: sorted,
    });

  const handleSort = useCallback((field) => setSort((prev) => toggleSort(prev, field)), []);
  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter(STATUS_ALL);
    setXmlFilter(XML_ALL);
    setTranspFilter("");
  };
  const activeFiltersCount = [statusFilter !== STATUS_ALL, xmlFilter !== XML_ALL, Boolean(transpFilter)].filter(Boolean).length;
  const hasFilters = normalizeText(searchTerm) !== "" || activeFiltersCount > 0;
  const empresaLabel = (empresa && (empresa.label || empresa.nome || empresa.Nome)) || "embarcador selecionado";
  const transportadoraLabel = (transportadora && (transportadora.label || transportadora.nome || transportadora.Nome)) || "";

  if (!hasEmpresaSelection) {
    return (
      <div className="content vincular-tipo-servico-page">
        <SpecificEmpresaRequiredNotice message="Selecione um embarcador específico para gerenciar os vínculos de tipo de serviço." />
      </div>
    );
  }

  const selectedTransp = transportadoras.find((o) => o.value === form.idTransportadora) || null;
  const selectedTipo = tiposServico.find((o) => o.value === form.idTipoServico) || null;
  const invalidSelect = (base, invalid) =>
    invalid ? { ...base, control: (p, s) => ({ ...base.control(p, s), borderColor: AUTOPILOT_COLORS.statusDanger }) } : base;
  const showState = loading || loadError || sorted.length === 0;

  return (
    <div className="content vincular-tipo-servico-page">
      <Modal isOpen={isFormOpen} toggle={closeForm} modalClassName="modal-align-top" fade={false} size="md">
        <ModalHeader toggle={closeForm} tag="h5">
          {editing ? "Editar vínculo" : "Novo vínculo"}
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <div className="cg-modal-subtitle">{empresaLabel}</div>
          <FormGroup>
            <Label for="vinculo-transportadora">Transportadora</Label>
            <Select
              inputId="vinculo-transportadora"
              classNamePrefix="react-select"
              styles={invalidSelect(reactSelectStyles, Boolean(formErrors.idTransportadora))}
              {...reactSelectPortalProps()}
              options={transportadoras}
              value={selectedTransp}
              onChange={(opt) => updateForm("idTransportadora", opt ? opt.value : "")}
              placeholder="Busque pelo nome da transportadora"
              noOptionsMessage={() => "Nenhuma transportadora vinculada a este embarcador"}
            />
            {formErrors.idTransportadora ? (
              <div className="invalid-feedback d-block">{formErrors.idTransportadora}</div>
            ) : null}
          </FormGroup>
          <FormGroup>
            <Label for="vinculo-tiposervico">Tipo de serviço</Label>
            <Select
              inputId="vinculo-tiposervico"
              classNamePrefix="react-select"
              styles={invalidSelect(reactSelectStyles, Boolean(formErrors.idTipoServico))}
              {...reactSelectPortalProps()}
              options={tiposServico}
              value={selectedTipo}
              onChange={(opt) => updateForm("idTipoServico", opt ? opt.value : "")}
              placeholder="Busque pelo código ou nome"
              noOptionsMessage={() => "Nenhum tipo de serviço ativo"}
            />
            {formErrors.idTipoServico ? <div className="invalid-feedback d-block">{formErrors.idTipoServico}</div> : null}
          </FormGroup>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="vinculo-status">Status</Label>
                <Input id="vinculo-status" type="select" value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="6" className="d-flex align-items-center">
              <FormGroup check style={{ marginTop: "0.5rem" }}>
                <Label check>
                  <Input type="checkbox" checked={form.xml} onChange={(e) => updateForm("xml", e.target.checked)} />
                  <span className="form-check-sign" />
                  Identificação por XML
                </Label>
              </FormGroup>
            </Col>
          </Row>
          <div className="cg-modal-footer">
            <Button color="default" outline size="sm" onClick={closeForm} disabled={saving}>
              Cancelar
            </Button>
            <Button color="info" size="sm" onClick={handleSave} disabled={saving} data-testid="salvar">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(toDelete)}
        title="Excluir vínculo"
        confirmLabel="Excluir"
        busyLabel="Excluindo..."
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      >
        Confirma a exclusão do vínculo entre <strong>{toDelete ? nomeTransportadora(toDelete) : ""}</strong> e{" "}
        <strong>{toDelete ? rotuloTipo(toDelete) : ""}</strong>? Esta ação não pode ser desfeita.
      </ConfirmModal>

      <PageHero
        eyebrow="Cadastros gerais"
        title="Vincular tipo de serviço"
        description={
          <>
            Quais tipos de serviço cada transportadora atende para <strong>{empresaLabel}</strong>, e se o tipo é
            identificado pelo XML.
            {hasTransportadoraSelection ? (
              <>
                {" "}
                Mostrando só <strong>{transportadoraLabel || `#${transportadoraId}`}</strong>, a transportadora escolhida no
                topo.
              </>
            ) : null}
          </>
        }
        actions={
          <>
            <PageHeroRefreshButton onClick={loadData} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={loading || sorted.length === 0} />
            <Button color="info" className="btn-sm" onClick={openCreate}>
              <FaPlus style={{ marginRight: 6 }} />
              Novo vínculo
            </Button>
          </>
        }
      />

      <MetricCardGroup>
        <MetricCard
          label="Vínculos"
          value={totais.total}
          helper={`${totais.transps} transportadora(s) com vínculo`}
          iconClassName="tim-icons icon-link-72"
          tone="info"
          onClick={clearFilters}
          active={!hasFilters}
        />
        <MetricCard
          label="Ativos"
          value={totais.ativos}
          helper="valendo hoje"
          iconClassName="tim-icons icon-check-2"
          tone="success"
          onClick={() => setStatusFilter((c) => (c === "active" ? STATUS_ALL : "active"))}
          active={statusFilter === "active"}
        />
        <MetricCard
          label="Inativos"
          value={totais.total - totais.ativos}
          helper="desligados"
          iconClassName="tim-icons icon-simple-remove"
          tone="neutral"
          onClick={() => setStatusFilter((c) => (c === "inactive" ? STATUS_ALL : "inactive"))}
          active={statusFilter === "inactive"}
        />
        <MetricCard
          label="Com identificação por XML"
          value={totais.comXml}
          helper="clique para filtrar"
          iconClassName="tim-icons icon-paper"
          tone="warning"
          onClick={() => setXmlFilter((c) => (c === "yes" ? XML_ALL : "yes"))}
          active={xmlFilter === "yes"}
        />
      </MetricCardGroup>

      <FilterBand
        mode="client"
        searchLabel="Buscar vínculo"
        searchHint="Transportadora, código ou nome do tipo de serviço"
        searchPlaceholder="Buscar por transportadora ou tipo de serviço"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Filtros"
        panelDescription="Refine por transportadora, identificação por XML e status."
        onClear={hasFilters ? clearFilters : undefined}
        showActions={hasFilters}
      >
        <Row>
          <Col md="4">
            <Label for="vinculo-transp-filter" style={{ color: AUTOPILOT_COLORS.muted }}>
              Transportadora
            </Label>
            <Select
              inputId="vinculo-transp-filter"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={transpOptions}
              value={transpOptions.find((o) => o.value === transpFilter) || TRANSP_ALL}
              onChange={(opt) => setTranspFilter(opt ? opt.value : "")}
            />
          </Col>
          <Col md="4">
            <Label for="vinculo-xml-filter" style={{ color: AUTOPILOT_COLORS.muted }}>
              Identificação por XML
            </Label>
            <Select
              inputId="vinculo-xml-filter"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={XML_OPTIONS}
              value={XML_OPTIONS.find((o) => o.value === xmlFilter) || XML_OPTIONS[0]}
              onChange={(opt) => setXmlFilter(opt ? opt.value : XML_ALL)}
              isSearchable={false}
            />
          </Col>
          <Col md="4">
            <Label for="vinculo-status-filter" style={{ color: AUTOPILOT_COLORS.muted }}>
              Status
            </Label>
            <Select
              inputId="vinculo-status-filter"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((o) => o.value === statusFilter) || STATUS_OPTIONS[0]}
              onChange={(opt) => setStatusFilter(opt ? opt.value : STATUS_ALL)}
              isSearchable={false}
            />
          </Col>
        </Row>
      </FilterBand>

      <ShellCard title="Vínculos cadastrados" description={`${sorted.length} resultado(s)`}>
        {showState ? (
          <ListState
            loading={loading}
            error={loadError}
            empty={sorted.length === 0}
            loadingText="Carregando vínculos…"
            emptyTitle="Nenhum vínculo encontrado"
            emptyText={hasFilters ? "Ajuste os filtros ou limpe a busca." : "Cadastre o primeiro vínculo pelo botão acima."}
            onRetry={loadData}
          />
        ) : (
          <TableSurface>
            <Table size="sm" responsive hover className="cg-table hub-thead-accent" data-testid="vinculo-tabela">
              <colgroup>
                <col style={{ width: "32%" }} />
                <col />
                <col style={{ width: 190 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 100 }} />
              </colgroup>
              <thead className="text-primary">
                <tr>
                  <th>
                    <SortHeader label="Transportadora" field="transportadora" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Tipo de serviço" field="tipo" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Identificação por XML" field="xml" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                  </th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paged.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{nomeTransportadora(item)}</strong>
                    </td>
                    <td>
                      {item.tipoServicoNome}
                      {item.tipoServicoCodigo ? <span className="cg-sub cg-mono">{item.tipoServicoCodigo}</span> : null}
                    </td>
                    <td>{isXml(item.xml) ? <Pill tone="info">Sim</Pill> : <Pill tone="neutral">Não</Pill>}</td>
                    <td>
                      <StatusPill active={isActive(item.status)} />
                    </td>
                    <td className="text-right">
                      <span className="cg-acoes">
                        <Button color="link" size="sm" className="btn-icon" title="Editar" onClick={() => openEdit(item)}>
                          <FaPen />
                        </Button>
                        <Button color="link" size="sm" className="btn-icon text-danger" title="Excluir" onClick={() => setToDelete(item)}>
                          <FaTrash />
                        </Button>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              total={paged.total}
              itemLabel="vínculo(s)"
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </TableSurface>
        )}
      </ShellCard>
    </div>
  );
};

export default VincularTipoServico;
